import { useCallback } from "react";
import { useScope } from "@state/scopeContext.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { AccrualImporterGroup } from "./useAccrualImporterState";
import { mapGroupToAccrualItem } from "./mapGroupsToAccrualItems";
import { AccrualMode } from "./types";

function mapGroupToPayloadItem(group: AccrualImporterGroup, mode: AccrualMode) {
    const item = mapGroupToAccrualItem(group);
    const { status, ...rest } = item;
    return {
        ...rest,
        status: status ?? "open",
        lines: item.lines.map(({ accrualAccountId, ...line }) => ({
            ...line,
            ...(mode === "bill"
                ? { accountsPayableId: accrualAccountId }
                : { accountsReceivableId: accrualAccountId }),
        })),
    };
}

export function useAccrualEditSubmit({
    mode,
    originalAccrualIds,
    mutateAsync,
    onClose,
    successMessage,
    errorMessage,
    onError,
}: {
    mode: AccrualMode;
    originalAccrualIds: string[];
    mutateAsync: (params: Record<string, unknown>) => Promise<unknown>;
    onClose: () => void;
    successMessage: string;
    errorMessage: string;
    /** Replaces the generic error toast when the caller presents errors itself. */
    onError?: (error: unknown) => void;
}) {
    const { workspaceId } = useScope();
    const { toast, error } = useToaster();

    const handleSubmit = useCallback(
        async (groups: AccrualImporterGroup[]) => {
            if (!workspaceId) {
                return;
            }

            const upserts = groups.map((group) =>
                mapGroupToPayloadItem(group, mode),
            );

            // idsToRemove = original bills/invoices the drawer opened with,
            // minus any that survived as a patch upsert. Newly-added groups
            // (no `id`) don't appear here; lines removed from a surviving
            // group are handled server-side via per-bill orphan deletion.
            const survivingIds = new Set(
                upserts
                    .map((u) => u.id)
                    .filter((id): id is string => id !== undefined),
            );
            const idsToRemove = originalAccrualIds.filter(
                (id) => !survivingIds.has(id),
            );

            try {
                await mutateAsync({
                    workspaceId,
                    idsToRemove,
                    upserts,
                });
                toast(successMessage);
                onClose();
            } catch (submitError) {
                if (onError) {
                    onError(submitError);
                    return;
                }
                error(errorMessage);
            }
        },
        [
            workspaceId,
            mode,
            originalAccrualIds,
            mutateAsync,
            toast,
            error,
            successMessage,
            errorMessage,
            onError,
            onClose,
        ],
    );

    return handleSubmit;
}
